import { api } from './client';

type Envelope<T> = { data: T };

export type EnvelopeStatus = 'draft' | 'sent' | 'in_progress' | 'completed' | 'declined' | 'expired' | 'voided';
export type EnvelopeWorkflow = 'sequential' | 'parallel' | 'hybrid';

export type EnvelopeDocument = {
  id: string;
  originalName: string;
  pagesCount: number;
  fileSize: string;
  orderIndex: number;
  sourceType: 'uploaded' | 'html';
};

export type EnvelopeRecipientSummary = {
  id: string;
  email: string;
  name: string;
  role: 'signer' | 'approver' | 'cc' | 'witness' | 'for_info';
  signingOrder: number;
  authMethod: 'email_otp' | 'sms_otp' | 'access_code' | 'asan_imza' | 'none';
  status: 'pending' | 'sent' | 'viewed' | 'signed' | 'declined' | 'expired';
  signedAt: string | null;
  declinedAt: string | null;
  declineReason: string | null;
};

export type EnvelopeView = {
  id: string;
  title: string;
  message: string | null;
  status: EnvelopeStatus;
  workflowType: EnvelopeWorkflow;
  signatureOptional: boolean;
  reminderEnabled: boolean;
  reminderDays: number | null;
  reminderHour: number | null;
  expiresAt: string | null;
  sentAt: string | null;
  completedAt: string | null;
  createdAt: string;
  updatedAt: string;
  documentNumber: string | null;
  numberingType: string | null;
  numberingDept: string | null;
  numberingForm: string | null;
  numberingRevision: number;
  numberingSchemeId: string | null;
  confidentiality: string | null;
  /** 'Internal' | 'External' — where the document originates. */
  documentSource: string | null;
  nextReviewDate: string | null;
  // 'algebra' = mirrored (read-only) from the linked Algebra DOC row;
  // 'manual' = edited natively here; null = never set.
  reviewDateSource: 'algebra' | 'manual' | null;
  archivedAt: string | null;
  changeDescription: string | null;
  reviewComments?: Array<{
    id: string;
    page: number;
    xPct?: number;
    yPct?: number;
    text: string;
    author: string;
    role: string;
    at: string;
  }>;
  folder: { id: string; name: string; color: string } | null;
  creator: { id: string; firstName: string; lastName: string; email: string };
  documents: EnvelopeDocument[];
  recipients: EnvelopeRecipientSummary[];
};

export type EnvelopeListResponse = {
  items: EnvelopeView[];
  total: number;
  page: number;
  perPage: number;
};

export type LineageNode = {
  id: string;
  revision: number;
  status: EnvelopeStatus;
  documentNumber: string | null;
  createdAt: string;
  completedAt: string | null;
  creator: { firstName: string; lastName: string; email: string };
  isCurrent: boolean;
};

export const envelopesApi = {
  async list(params: {
    status?: string;
    search?: string;
    folder?: string;
    page?: number;
    perPage?: number;
    archive?: boolean;
    library?: boolean;
    excludeDeclined?: boolean;
    numberingSchemeId?: string;
  } = {}): Promise<EnvelopeListResponse> {
    const { data } = await api.get<Envelope<EnvelopeListResponse>>('/envelopes', {
      params: {
        status: params.status,
        search: params.search,
        folder: params.folder,
        page: params.page,
        per_page: params.perPage,
        ...(params.archive ? { archive: 'true' } : {}),
        ...(params.library ? { library: 'true' } : {}),
        ...(params.excludeDeclined ? { exclude_declined: 'true' } : {}),
        ...(params.numberingSchemeId ? { scheme: params.numberingSchemeId } : {}),
      },
    });
    return data.data;
  },

  async get(uuid: string): Promise<EnvelopeView> {
    const { data } = await api.get<Envelope<EnvelopeView>>(`/envelopes/${uuid}`);
    return data.data;
  },

  async create(input: {
    title: string;
    message?: string;
    workflowType?: EnvelopeWorkflow;
    expiresAt?: string;
    signatureOptional?: boolean;
    numberingSchemeId?: string | null;
  }): Promise<EnvelopeView> {
    const { data } = await api.post<Envelope<EnvelopeView>>('/envelopes', input);
    return data.data;
  },

  async update(
    uuid: string,
    input: Partial<{
      title: string;
      message: string;
      workflowType: EnvelopeWorkflow;
      signatureOptional: boolean;
      reminderEnabled: boolean;
      reminderDays: number | null;
      reminderHour: number | null;
      expiresAt: string;
      numberingType: string | null;
      numberingDept: string | null;
      numberingForm: string | null;
      numberingSchemeId: string | null;
      confidentiality: string | null;
      documentSource: string | null;
      folderId: string | null;
    }>,
  ): Promise<EnvelopeView> {
    const { data } = await api.patch<Envelope<EnvelopeView>>(`/envelopes/${uuid}`, input);
    return data.data;
  },

  async delete(uuid: string): Promise<void> {
    await api.delete(`/envelopes/${uuid}`);
  },

  async newRevision(
    uuid: string,
    payload: { changeDescription?: string } = {},
  ): Promise<EnvelopeView> {
    const { data } = await api.post<Envelope<EnvelopeView>>(
      `/envelopes/${uuid}/new-revision`,
      payload,
    );
    return data.data;
  },

  async lineage(uuid: string): Promise<LineageNode[]> {
    const { data } = await api.get<Envelope<LineageNode[]>>(`/envelopes/${uuid}/lineage`);
    return data.data;
  },

  /**
   * Set the Documentology-native "next review date" (used when the company has
   * no Algebra register to source it from). Pass '' or null to clear it. When
   * the row is sourced from Algebra (reviewDateSource === 'algebra') the UI keeps
   * this read-only, so the frontend won't call this for those rows.
   */
  async setReviewDate(uuid: string, date: string | null): Promise<EnvelopeView> {
    const { data } = await api.put<Envelope<EnvelopeView>>(
      `/envelopes/${uuid}/review-date`,
      { date },
    );
    return data.data;
  },

  async send(uuid: string): Promise<EnvelopeView> {
    const { data } = await api.post<Envelope<EnvelopeView>>(`/envelopes/${uuid}/send`);
    return data.data;
  },

  /** Envelopes addressed to the current user (recipient by email match). */
  async exportCsv(params: { status?: string; search?: string; folder?: string; archive?: boolean; library?: boolean; excludeDeclined?: boolean; numberingSchemeId?: string } = {}): Promise<void> {
    return downloadEnvelopeExport('csv', params);
  },

  async exportPdf(params: { status?: string; search?: string; folder?: string; archive?: boolean; library?: boolean; excludeDeclined?: boolean; numberingSchemeId?: string } = {}): Promise<void> {
    return downloadEnvelopeExport('pdf', params);
  },

  async exportXlsx(params: { status?: string; search?: string; folder?: string; archive?: boolean; library?: boolean; excludeDeclined?: boolean; numberingSchemeId?: string } = {}): Promise<void> {
    return downloadEnvelopeExport('xlsx', params);
  },

  async inbox(): Promise<EnvelopeView[]> {
    const { data } = await api.get<Envelope<EnvelopeView[]>>('/envelopes/inbox');
    return data.data;
  },

  /** Issue a fresh magic-link sign token for the current user. */
  async takeSignLink(uuid: string): Promise<{ token: string }> {
    const { data } = await api.post<Envelope<{ token: string }>>(`/envelopes/${uuid}/take-sign-link`);
    return data.data;
  },

  async void(uuid: string, reason?: string): Promise<EnvelopeView> {
    const { data } = await api.post<Envelope<EnvelopeView>>(`/envelopes/${uuid}/void`, { reason });
    return data.data;
  },

  async remind(uuid: string, recipientId?: string): Promise<{ remindersSent: number }> {
    const { data } = await api.post<Envelope<{ remindersSent: number }>>(
      `/envelopes/${uuid}/remind`,
      { recipientId },
    );
    return data.data;
  },

  async listAudit(uuid: string): Promise<AuditEntry[]> {
    const { data } = await api.get<Envelope<AuditEntry[]>>(`/envelopes/${uuid}/audit`);
    return data.data;
  },

  /** Force-download the completion certificate PDF. */
  async downloadCertificate(uuid: string, filename: string): Promise<void> {
    const res = await api.get<Blob>(`/envelopes/${uuid}/certificate`, { responseType: 'blob' });
    const url = URL.createObjectURL(res.data);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};

export type AuditEntry = {
  id: string;
  action: string;
  actorEmail: string | null;
  ipAddress: string | null;
  userAgent: string | null;
  metadata: unknown;
  createdAt: string;
};

async function downloadEnvelopeExport(
  format: 'csv' | 'xlsx' | 'pdf',
  params: { status?: string; search?: string; folder?: string; archive?: boolean; library?: boolean; excludeDeclined?: boolean; numberingSchemeId?: string },
): Promise<void> {
  const res = await api.get<Blob>(`/envelopes/export.${format}`, {
    params: {
      status: params.status,
      search: params.search,
      folder: params.folder,
      ...(params.archive ? { archive: 'true' } : {}),
      ...(params.library ? { library: 'true' } : {}),
      ...(params.excludeDeclined ? { exclude_declined: 'true' } : {}),
      ...(params.numberingSchemeId ? { scheme: params.numberingSchemeId } : {}),
    },
    responseType: 'blob',
  });
  const url = URL.createObjectURL(res.data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `envelopes-${new Date().toISOString().slice(0, 10)}.${format}`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
